<script setup lang="ts">
  import type { PropType } from 'vue'

  import { computed, onMounted, ref, watch } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { useRoute } from 'nuxt/app'

  import { XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils/core.utils'

  import { useLogger } from '@common/core-layer/composables/useLogger'

  import { getLocalStorageItem, setLocalStorageItem } from '@common/core-layer/utils/storage.helper'

  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import CPText from '@ui/components/CPText.vue'
  import type { CourseResult, LessonResult, SearchItem } from '@courses/types-layer/types/search'

  import { COURSES_SEARCH_HISTORY_KEY } from '../constants/search-history'

  import { useSearch } from '../composables/useSearch'

  import CourseResultCard from './CourseResultCard.vue'

  import EmptySearchState from './EmptySearchState.vue'

  import LessonResultCard from './LessonResultCard.vue'

  import SearchLoading from './SearchLoading.vue'

  const props = defineProps({
    query: {
      type: String,
      default: undefined,
    },
    variant: {
      type: String as PropType<'page' | 'dropdown'>,
      default: 'page',
    },
  })

  const emit = defineEmits<{
    clear: []
    recentSearchesChange: [remainingCount: number]
    selectQuery: [query: string]
  }>()

  const MAX_HISTORY_QUERY_LENGTH = 200

  const route = useRoute()

  const { t } = useI18n()
  const { logger } = useLogger()

  const { CPNavigateTo } = useCPNavigation()

  const { createSearchQuery } = useSearch()

  const isDropdown = computed(() => props.variant === 'dropdown')
  const searchQuery = computed(() => props.query ?? (route.query.q as string) ?? '')

  const searchResults = createSearchQuery(searchQuery)

  const { isLoading, isFetching, isPending } = searchResults
  const isSearchInFlight = computed(() => isLoading.value || isFetching.value || isPending.value)

  const recentSearches = ref<string[]>([])

  const readSearchHistory = () => {
    const stored = getLocalStorageItem(COURSES_SEARCH_HISTORY_KEY)
    if (!stored) return []

    try {
      const parsed: unknown = JSON.parse(stored)
      if (!Array.isArray(parsed)) return []
      return parsed.filter((item): item is string => typeof item === 'string')
    } catch (error) {
      logger.warn('Unable to parse courses search history', {
        error: error instanceof Error ? error : new Error(String(error)),
      })
      return []
    }
  }

  const courseResults = computed<CourseResult[]>(() => {
    const items = searchResults.searchItems.value || []
    if (!Array.isArray(items)) return []
    // API returns type: 'product' only for courses (not categories)
    // Categories are modules within courses, not standalone courses
    return items
      .filter((item: SearchItem) => item.type === 'product')
      .map((item: SearchItem) => ({
        _id: item.typeId,
        title: item.title,
        description: item.description || '',
        posterImage: item.thumbnail || '', // API uses 'thumbnail' field
        totalPosts: item.totalPosts,
        completedPosts: item.completedPosts,
        author: item.author,
        membersCount: item.membersCount,
        updatedAt: item.updatedAt,
      }))
  })

  const lessonResults = computed<LessonResult[]>(() => {
    const items = searchResults.searchItems.value || []
    const titles = searchResults.productTitles.value || {}
    if (!Array.isArray(items)) return []
    // Posts are lessons
    return items
      .filter((item: SearchItem) => item.type === 'post')
      .map((item: SearchItem) => ({
        _id: item.typeId,
        title: item.title,
        description: item.description || '',
        productId: item.productId,
        productTitle: titles[item.productId] || '',
        categoryId: item.categoryId || undefined,
        lessonNumber: item.lessonNumber,
        thumbnail: item.thumbnail || undefined,
        contentType: item.contentType || undefined,
      }))
  })

  const hasResults = computed(() => {
    // Only show results if we have a search query
    if (!searchQuery.value || !searchQuery.value.trim()) {
      return false
    }
    return courseResults.value.length > 0 || lessonResults.value.length > 0
  })

  const showEmptyState = computed(() => {
    const query = searchQuery.value?.trim()
    return Boolean(query) && !hasResults.value && !isSearchInFlight.value
  })

  const showLoading = computed(() => {
    if (isLoading.value) return true
    const query = searchQuery.value?.trim()
    return Boolean(query) && !hasResults.value && isSearchInFlight.value
  })

  const displayedCourses = computed(() => (isDropdown.value ? courseResults.value : courseResults.value.slice(0, 5)))

  const displayedLessons = computed(() => (isDropdown.value ? lessonResults.value : lessonResults.value.slice(0, 5)))

  const hasMoreCourses = computed(() => !isDropdown.value && courseResults.value.length > 5)

  const hasMoreLessons = computed(() => !isDropdown.value && lessonResults.value.length > 5)

  const saveToHistory = (query: string) => {
    const trimmed = query.trim()
    if (!trimmed || trimmed.length > MAX_HISTORY_QUERY_LENGTH) return

    const history = readSearchHistory()
    const filtered = history.filter(item => item !== trimmed)
    const updated = [trimmed, ...filtered].slice(0, 10)

    setLocalStorageItem(COURSES_SEARCH_HISTORY_KEY, JSON.stringify(updated))
    recentSearches.value = updated
  }

  const removeSearch = (query: string, event: Event) => {
    event.stopPropagation()
    const history = readSearchHistory()
    const updated = history.filter(item => item !== query)
    setLocalStorageItem(COURSES_SEARCH_HISTORY_KEY, JSON.stringify(updated))
    recentSearches.value = updated
    if (isDropdown.value) {
      emit('recentSearchesChange', updated.length)
    }
  }

  const clickSearch = async (query: string) => {
    if (isDropdown.value) {
      emit('selectQuery', query)
      return
    }

    await CPNavigateTo({
      name: 'courses-search',
      query: { q: query },
    })
  }

  const handleSeeAllCourses = async () => {
    await CPNavigateTo({
      name: 'courses-search',
      query: {
        ...route.query,
        filter: 'courses',
      },
    })
  }

  const handleSeeAllLessons = async () => {
    await CPNavigateTo({
      name: 'courses-search',
      query: {
        ...route.query,
        filter: 'lessons',
      },
    })
  }

  const handleClearEmpty = async () => {
    if (isDropdown.value) {
      emit('clear')
      return
    }

    await CPNavigateTo({
      name: 'courses-search',
    })
  }

  onMounted(() => {
    recentSearches.value = readSearchHistory()
  })

  // Save to history when search completes successfully
  watch([searchQuery, () => searchResults.searchItems.value], ([query, items]) => {
    if (!isDropdown.value && query && query.trim() && items && items.length > 0) {
      saveToHistory(query)
    }
  })
</script>

<template>
  <div :id="createElementId('search', 'results', 'container')" class="w-full">
    <!-- Loading State -->
    <SearchLoading v-if="showLoading" />

    <!-- Empty State -->
    <EmptySearchState
      v-else-if="showEmptyState"
      :search-query="searchQuery"
      :variant="isDropdown ? 'dropdown' : 'page'"
      @clear="handleClearEmpty"
    />

    <!-- Results -->
    <div v-else-if="hasResults">
      <!-- Courses Section -->
      <div v-if="courseResults.length > 0" class="mb-4">
        <!-- Section Header -->
        <div class="flex items-center justify-between px-4 py-3">
          <CPText size="xs" class="text-secondary uppercase">
            {{ t('courses.search.searchResults.coursesSection', { count: courseResults.length }) }}
          </CPText>
          <CPText
            v-if="hasMoreCourses"
            :id="createElementId('search-results', 'courses', 'see-all')"
            size="md"
            weight="semibold"
            class="text-primary cursor-pointer"
            @click="handleSeeAllCourses"
          >
            {{ t('courses.search.searchResults.seeAll') }}
          </CPText>
        </div>

        <!-- Horizontal Scrolling Courses -->
        <div class="scrollbar-hide overflow-x-auto">
          <div class="flex gap-4 px-4 pb-4">
            <CourseResultCard v-for="course in displayedCourses" :key="course._id" :course="course" />
          </div>
        </div>
      </div>

      <!-- Lessons Section -->
      <div v-if="lessonResults.length > 0">
        <!-- Section Header -->
        <div class="flex items-center justify-between px-4 py-3">
          <CPText size="xs" class="text-secondary uppercase">
            {{ t('courses.search.searchResults.lessonsSection', { count: lessonResults.length }) }}
          </CPText>
          <CPText
            v-if="hasMoreLessons"
            :id="createElementId('search-results', 'lessons', 'see-all')"
            size="md"
            weight="semibold"
            class="text-primary cursor-pointer"
            @click="handleSeeAllLessons"
          >
            {{ t('courses.search.searchResults.seeAll') }}
          </CPText>
        </div>

        <!-- Vertical Lessons List -->
        <div class="flex flex-col gap-2 px-4">
          <LessonResultCard v-for="lesson in displayedLessons" :key="lesson._id" :lesson="lesson" />
        </div>
      </div>
    </div>

    <!-- Recent Searches (no search text) -->
    <div v-else-if="recentSearches.length > 0" class="px-4 py-3">
      <CPText size="xs" class="text-secondary mb-3 uppercase">
        {{ t('courses.search.searchResults.recentSection') }}
      </CPText>

      <div class="mt-3 flex flex-wrap gap-2">
        <div
          v-for="(search, index) in recentSearches"
          :id="createElementId('search', 'recent', `item-${index}`)"
          :key="index"
          role="button"
          tabindex="0"
          class="border-default hover:bg-hover group flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5"
          @click="clickSearch(search)"
          @keydown.enter.prevent="clickSearch(search)"
          @keydown.space.prevent="clickSearch(search)"
        >
          <CPText size="md" class="text-body whitespace-nowrap">
            {{ search }}
          </CPText>
          <XCloseIcon
            :id="createElementId('search', 'recent', `remove-${index}`)"
            class="text-body h-3 w-3 flex-shrink-0 cursor-pointer"
            @click="(e: Event) => removeSearch(search, e)"
          />
        </div>
      </div>
    </div>

    <!-- Initial State (no search yet, no history) -->
    <div v-else-if="!isDropdown" class="flex flex-col items-center justify-center px-4 py-16 text-center">
      <CPText size="md" weight="regular" class="text-secondary">
        {{ t('courses.search.searchResults.initialState') }}
      </CPText>
    </div>
  </div>
</template>

<style scoped>
  /* Hide scrollbar for horizontal scroll */
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
</style>
